import { supplierContract } from '@montugroup/warehousing-contracts';
import { tsRestFetchApi } from '@ts-rest/core';
import type { TsRestReactQueryHooksContainer } from '@ts-rest/react-query/v5';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import type { FC, JSX, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { apiBaseUrl } from '~/constants/env';
import { Logger } from '~/lib/logger';
import { useAuth } from './AuthProvider';

const logger = new Logger('ApiProvider');

type ApiContextProps = {
  warehouseApi: {
    ReactQueryProvider: ({ children }: React.PropsWithChildren) => JSX.Element;
  } & TsRestReactQueryHooksContainer<
    typeof supplierContract,
    { baseUrl: string; baseHeaders: { 'Content-Type': string; Authorization?: string } }
  >;
};

const ApiContext = createContext<ApiContextProps | undefined>(undefined);

export const ApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { token, logout } = useAuth();

  const warehouseApi = useMemo(() => {
    return initTsrReactQuery(supplierContract, {
      baseUrl: `${apiBaseUrl()}/warehousing`,
      baseHeaders: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      api: async (args) => {
        return tsRestFetchApi(args).then((res) => {
          // We only want to potentially logout the user for requests that require authentication
          if (!token) {
            return res;
          }

          // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
          if (res.status === 401) {
            logger.error('Unauthorized request');
            logout();
          }
          return res;
        });
      }
    });
  }, [logout, token]);

  return (
    <ApiContext.Provider value={{ warehouseApi }}>
      <warehouseApi.ReactQueryProvider>{children}</warehouseApi.ReactQueryProvider>
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};
